import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'react-hot-toast';
import { FaCheck, FaTimes, FaSpinner, FaCamera, FaUpload, FaFileAlt, FaTrash, FaMobileAlt, FaLock, FaDownload } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function CandidateRegisterForm() {
  const { supervisorId } = useParams();
  const navigate = useNavigate();
  const [supervisor, setSupervisor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  
  // Document upload state
  const [documents, setDocuments] = useState({
    aadhar: null,
    school_certificate: null,
    educational_certificate: null,
    bank_passbook: null,
    candidate_image: null,
    pancard: null,
    signature: null
  });
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraDocumentType, setCameraDocumentType] = useState(null);
  const videoRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    qualification: '',
    course: '',
    referral_code: '',
    establishment_name: '',
    month: '',
    punching_employee_code: '',
    aadhar_no: '',
    date_of_birth: '',
    gender: '',
    father_name: '',
    mother_name: '',
    category: '',
    address: '',
    district: '',
    state: '',
    pincode: '',
    institute_name: '',
    specialization: '',
    bank_name: '',
    account_no: '',
    ifsc_code: '',
    date_of_joining: '',
    stipend: '',
    job_role: ''
  });

  useEffect(() => {
    const verifySupervisor = async () => {
      try {
        console.log('Verifying supervisor with ID:', supervisorId);
        console.log('API URL:', API_URL);
        const response = await fetch(`${API_URL}/api/supervisor/public/${supervisorId}/verify`);
        console.log('Verify response status:', response.status);
        const data = await response.json();
        console.log('Verify response data:', data);
        
        if (data.success) {
          setSupervisor(data.supervisor);
          // Auto-fill referral code, establishment name, and current month
          const currentDate = new Date();
          const currentMonth = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
          
          setFormData(prev => ({
            ...prev,
            referral_code: data.supervisor.referral_code || '',
            establishment_name: data.supervisor.company?.name || data.supervisor.company?.company_name || '',
            month: currentMonth
          }));
        } else {
          toast.error(data.message || 'Invalid registration link');
          setTimeout(() => navigate('/'), 3000);
        }
      } catch (error) {
        console.error('Error verifying supervisor:', error);
        toast.error('Failed to verify registration link: ' + error.message);
        setTimeout(() => navigate('/'), 3000);
      } finally {
        setLoading(false);
      }
    };

    if (supervisorId) {
      verifySupervisor();
    } else {
      setLoading(false);
    }
  }, [supervisorId, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Document upload handlers
  const handleDocumentChange = (docType, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid file (Image, PDF, or DOCX)');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size should be less than 10MB');
      return;
    }
    
    setDocuments(prev => ({
      ...prev,
      [docType]: file
    }));
    toast.success(`${docType.replace('_', ' ')} uploaded successfully`);
  };

  const handleRemoveDocument = (docType) => {
    setDocuments(prev => ({
      ...prev,
      [docType]: null
    }));
  };

  // Camera functions
  const startCamera = async (docType) => {
    try {
      // Use back camera for documents, front camera for candidate photo
      const facingMode = docType === 'candidate_image' ? 'user' : 'environment';
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      setCameraStream(stream);
      setCameraDocumentType(docType);
      setShowCameraModal(true);
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCameraModal(false);
    setCameraDocumentType(null);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);
    
    canvas.toBlob((blob) => {
      const file = new File([blob], `captured_${Date.now()}.jpg`, { type: 'image/jpeg' });
      handleDocumentChange(cameraDocumentType, file);
      stopCamera();
      toast.success('Photo captured successfully!');
    }, 'image/jpeg', 0.8);
  };

  useEffect(() => {
    if (showCameraModal && videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream;
    }
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [showCameraModal, cameraStream]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    const requiredFields = [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'mobile', label: 'Mobile Number' },
      { key: 'password', label: 'Password' },
      { key: 'qualification', label: 'Qualification' },
      { key: 'course', label: 'Course' },
      { key: 'date_of_birth', label: 'Date of Birth' },
      { key: 'gender', label: 'Gender' },
      { key: 'father_name', label: 'Father Name' },
      { key: 'address', label: 'Address' }
    ];

    const missingFields = requiredFields.filter(field => !formData[field.key] || formData[field.key].trim() === '');
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Validate mobile number (should be 10 digits)
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(formData.mobile)) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    // Validate password (minimum 6 characters)
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setSubmitting(true);

    try {
      console.log('Submitting registration for supervisor:', supervisorId);
      console.log('Form data:', formData);
      console.log('Documents:', documents);
      console.log('API URL:', API_URL);
      
      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Add documents
      if (documents.aadhar) formDataToSend.append('aadhar', documents.aadhar);
      if (documents.school_certificate) formDataToSend.append('school_certificate', documents.school_certificate);
      if (documents.educational_certificate) formDataToSend.append('educational_certificate', documents.educational_certificate);
      if (documents.bank_passbook) formDataToSend.append('bank_passbook', documents.bank_passbook);
      if (documents.candidate_image) formDataToSend.append('candidate_image', documents.candidate_image);
      if (documents.pancard) formDataToSend.append('pancard', documents.pancard);
      if (documents.signature) formDataToSend.append('signature', documents.signature);
      
      const response = await fetch(`${API_URL}/api/supervisor/public/${supervisorId}/register-candidate`, {
        method: 'POST',
        body: formDataToSend
      });

      console.log('Registration response status:', response.status);
      const data = await response.json();
      console.log('Registration response data:', data);

      if (data.success) {
        // Store registration data for success page
        setRegistrationData({
          mobile: formData.mobile,
          password: formData.password,
          name: formData.name
        });
        setRegistrationSuccess(true);
      } else {
        toast.error(data.message || 'Registration failed');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Failed to submit registration: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{
          background: '#fff',
          padding: 40,
          borderRadius: 12,
          textAlign: 'center',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
        }}>
          <FaSpinner style={{ fontSize: 48, color: '#667eea', animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: 16, color: '#6b7280' }}>Verifying registration link...</p>
        </div>
      </div>
    );
  }

  if (!supervisor) {
    return null;
  }

  // Success Page Component
  if (registrationSuccess && registrationData) {
    const handlePlayStoreClick = () => {
      // Play Store URL for Go Gig Candidate app
      const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.gigzd.partner';
      window.open(playStoreUrl, '_blank');
    };

    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Toaster position="top-center" />
        <div style={{
          maxWidth: isMobile ? '100%' : 600,
          width: '100%',
          background: '#fff',
          borderRadius: 16,
          padding: isMobile ? 24 : 40,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          {/* Success Icon */}
          <div style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)'
          }}>
            <FaCheck style={{ fontSize: 40, color: '#fff' }} />
          </div>

          {/* Success Message */}
          <h1 style={{
            color: '#667eea',
            fontSize: isMobile ? 28 : 32,
            fontWeight: 700,
            margin: '0 0 12px 0'
          }}>
            Registration Successful!
          </h1>
          <p style={{
            color: '#6b7280',
            fontSize: isMobile ? 14 : 16,
            margin: '0 0 32px 0',
            lineHeight: 1.6
          }}>
            Your registration has been completed successfully. Please download our mobile app to get started.
          </p>

          {/* Play Store Download Button */}
          <div style={{
            marginBottom: 32
          }}>
            <button
              onClick={handlePlayStoreClick}
              style={{
                width: '100%',
                padding: '16px 24px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                fontSize: isMobile ? 16 : 18,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 12px -1px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
              }}
            >
              <FaDownload style={{ fontSize: 20 }} />
              <span>Download from Play Store</span>
            </button>
          </div>

          {/* Instructions */}
          <div style={{
            background: '#f0f4ff',
            padding: '20px',
            borderRadius: 12,
            marginBottom: 24,
            textAlign: 'left'
          }}>
            <h3 style={{
              color: '#667eea',
              fontSize: isMobile ? 16 : 18,
              fontWeight: 600,
              margin: '0 0 12px 0',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <FaCheck style={{ color: '#10b981' }} />
              Next Steps:
            </h3>
            <ol style={{
              color: '#4b5563',
              fontSize: isMobile ? 14 : 15,
              lineHeight: 1.8,
              margin: 0,
              paddingLeft: 20
            }}>
              <li>Click the button above to download our mobile app from Play Store</li>
              <li>Once downloaded, open the app and click on <strong>"Login"</strong></li>
              <li>Use your credentials below to log in</li>
            </ol>
          </div>

          {/* Login Credentials */}
          <div style={{
            background: '#fffbeb',
            border: '2px solid #fef3c7',
            padding: '20px',
            borderRadius: 12,
            marginBottom: 24
          }}>
            <h3 style={{
              color: '#92400e',
              fontSize: isMobile ? 16 : 18,
              fontWeight: 600,
              margin: '0 0 16px 0'
            }}>
              Your Login Credentials
            </h3>
            
            {/* Mobile Number */}
            <div style={{
              marginBottom: 16,
              textAlign: 'left'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
                color: '#78350f',
                fontSize: isMobile ? 13 : 14,
                fontWeight: 600
              }}>
                <FaMobileAlt style={{ color: '#667eea' }} />
                Mobile Number:
              </div>
              <div style={{
                background: '#fff',
                padding: '12px 16px',
                borderRadius: 8,
                border: '2px solid #e5e7eb',
                fontSize: isMobile ? 16 : 18,
                fontWeight: 600,
                color: '#1f2937',
                fontFamily: 'monospace',
                letterSpacing: '1px'
              }}>
                {registrationData.mobile}
              </div>
            </div>

            {/* Password */}
            <div style={{
              textAlign: 'left'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
                color: '#78350f',
                fontSize: isMobile ? 13 : 14,
                fontWeight: 600
              }}>
                <FaLock style={{ color: '#667eea' }} />
                Password:
              </div>
              <div style={{
                background: '#fff',
                padding: '12px 16px',
                borderRadius: 8,
                border: '2px solid #e5e7eb',
                fontSize: isMobile ? 16 : 18,
                fontWeight: 600,
                color: '#1f2937',
                fontFamily: 'monospace',
                letterSpacing: '1px'
              }}>
                {registrationData.password}
              </div>
            </div>
          </div>

          {/* Important Note */}
          <div style={{
            background: '#fef2f2',
            border: '2px solid #fee2e2',
            padding: '16px',
            borderRadius: 8,
            marginBottom: 24
          }}>
            <p style={{
              color: '#991b1b',
              fontSize: isMobile ? 12 : 13,
              margin: 0,
              lineHeight: 1.6
            }}>
              <strong>Important:</strong> Please save these credentials securely. You'll need them to log in to the mobile app.
            </p>
          </div>

          {/* Close Button */}
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '12px 24px',
              background: '#e5e7eb',
              color: '#374151',
              border: 'none',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#d1d5db'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#e5e7eb'}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Required fields (basic information)
  const requiredFields = [
    { key: 'name', label: 'Name', type: 'text', required: true },
    { key: 'email', label: 'Email', type: 'email', required: true },
    { key: 'mobile', label: 'Mobile Number', type: 'tel', required: true },
    { key: 'password', label: 'Password', type: 'password', required: true },
    { key: 'qualification', label: 'Qualification', type: 'text', required: true },
    { key: 'course', label: 'Course', type: 'text', required: true },
    { key: 'date_of_birth', label: 'Date of Birth', type: 'date', required: true },
    { key: 'gender', label: 'Gender', type: 'select', options: ['Male', 'Female', 'Other'], required: true },
    { key: 'father_name', label: 'Father Name', type: 'text', required: true },
    { key: 'address', label: 'Address', type: 'textarea', required: true }
  ];

  // Other Details section
  const otherDetailsFields = [
    { key: 'referral_code', label: 'Referral Code', type: 'text', required: false },
    { key: 'establishment_name', label: 'Establishment Name', type: 'text', required: false },
    { key: 'month', label: 'Month', type: 'text', required: false },
    { key: 'punching_employee_code', label: 'Punching/Employee Code', type: 'text', required: false },
    { key: 'aadhar_no', label: 'Aadhar Number', type: 'text', required: false },
    { key: 'mother_name', label: 'Mother Name', type: 'text', required: false },
    { key: 'category', label: 'Category', type: 'text', required: false },
    { key: 'district', label: 'District', type: 'text', required: false },
    { key: 'state', label: 'State', type: 'text', required: false },
    { key: 'pincode', label: 'Pincode', type: 'text', required: false },
    { key: 'institute_name', label: 'Institute Name', type: 'text', required: false },
    { key: 'specialization', label: 'Specialization', type: 'text', required: false },
    { key: 'date_of_joining', label: 'Date of Joining', type: 'date', required: false },
    { key: 'stipend', label: 'Stipend', type: 'number', required: false },
    { key: 'job_role', label: 'Job Role', type: 'text', required: false }
  ];

  // Bank Details section
  const bankDetailsFields = [
    { key: 'bank_name', label: 'Bank Name', type: 'text', required: false },
    { key: 'account_no', label: 'Account Number', type: 'text', required: false },
    { key: 'ifsc_code', label: 'IFSC Code', type: 'text', required: false }
  ];

  // Helper function to render a field
  const renderField = (field) => (
    <div key={field.key} style={field.type === 'textarea' ? { gridColumn: '1 / -1' } : {}}>
      <label style={{
        display: 'block',
        marginBottom: 8,
        fontWeight: 600,
        color: '#374151',
        fontSize: 14
      }}>
        {field.label}
        {field.required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
        {!field.required && <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 4 }}>(Optional)</span>}
      </label>
      {field.type === 'textarea' ? (
        <textarea
          value={formData[field.key]}
          onChange={(e) => handleInputChange(field.key, e.target.value)}
          rows={3}
          required={field.required}
          style={{
            width: '100%',
            padding: '12px',
            border: `2px solid ${formData[field.key] || !field.required ? '#e5e7eb' : '#ef4444'}`,
            borderRadius: 8,
            fontSize: 14,
            fontFamily: 'inherit',
            resize: 'vertical',
            transition: 'border-color 0.2s'
          }}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => {
            if (field.required && !formData[field.key]) {
              e.target.style.borderColor = '#ef4444';
            } else {
              e.target.style.borderColor = '#e5e7eb';
            }
          }}
        />
      ) : field.type === 'select' ? (
        <select
          value={formData[field.key]}
          onChange={(e) => handleInputChange(field.key, e.target.value)}
          required={field.required}
          style={{
            width: '100%',
            padding: '12px',
            border: `2px solid ${formData[field.key] || !field.required ? '#e5e7eb' : '#ef4444'}`,
            borderRadius: 8,
            fontSize: 14,
            fontFamily: 'inherit',
            background: '#fff',
            transition: 'border-color 0.2s'
          }}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => {
            if (field.required && !formData[field.key]) {
              e.target.style.borderColor = '#ef4444';
            } else {
              e.target.style.borderColor = '#e5e7eb';
            }
          }}
        >
          <option value="">Select {field.label}</option>
          {field.options?.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      ) : (
        <input
          type={field.type}
          value={formData[field.key]}
          onChange={(e) => handleInputChange(field.key, e.target.value)}
          required={field.required}
          style={{
            width: '100%',
            padding: '12px',
            border: `2px solid ${formData[field.key] || !field.required ? '#e5e7eb' : '#ef4444'}`,
            borderRadius: 8,
            fontSize: 14,
            fontFamily: 'inherit',
            transition: 'border-color 0.2s'
          }}
          onFocus={(e) => e.target.style.borderColor = '#667eea'}
          onBlur={(e) => {
            if (field.required && !formData[field.key]) {
              e.target.style.borderColor = '#ef4444';
            } else {
              e.target.style.borderColor = '#e5e7eb';
            }
          }}
        />
      )}
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '20px 0',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Toaster position="top-center" />
      <div style={{
        maxWidth: isMobile ? '100%' : 1200,
        margin: '0 auto',
        padding: isMobile ? '0 12px' : '0 20px',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 40px)',
        overflow: 'hidden'
      }}>
        {/* Fixed Header Section */}
        <div style={{
          background: '#fff',
          borderRadius: isMobile ? '12px 12px 0 0' : '16px 16px 0 0',
          padding: isMobile ? '16px 20px' : '24px 40px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          marginTop: isMobile ? 10 : 20,
          flexShrink: 0,
          zIndex: 1000,
          position: 'sticky',
          top: 0
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: 20
          }}>
            <h1 style={{
              color: '#667eea',
              fontSize: isMobile ? 24 : 32,
              fontWeight: 700,
              margin: '0 0 8px 0'
            }}>
              Candidate Registration
            </h1>
            <p style={{
              color: '#6b7280',
              fontSize: isMobile ? 14 : 16,
              margin: 0
            }}>
              Registering with supervisor: <strong>{supervisor.name}</strong>
            </p>
            <p style={{
              color: '#9ca3af',
              fontSize: isMobile ? 12 : 14,
              margin: '8px 0 0 0'
            }}>
              Fill in your details below. Fields marked with <span style={{ color: '#ef4444' }}>*</span> are required.
            </p>
          </div>

          {/* Submit Button at Top */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            paddingBottom: 16,
            borderBottom: '2px solid #e5e7eb'
          }}>
            <button
              type="submit"
              form="candidate-registration-form"
              disabled={submitting}
              style={{
                padding: '14px 32px',
                background: submitting ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 600,
                cursor: submitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.2s',
                boxShadow: submitting ? 'none' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            >
              {submitting ? (
                <>
                  <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                  Submitting...
                </>
              ) : (
                <>
                  <FaCheck />
                  Submit Registration
                </>
              )}
            </button>
          </div>
        </div>

        {/* Scrollable Form Content */}
        <div style={{
          background: '#fff',
          borderRadius: isMobile ? '0 0 12px 12px' : '0 0 16px 16px',
          padding: isMobile ? '20px' : '40px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
          <form id="candidate-registration-form" onSubmit={handleSubmit}>
            {/* Required Fields Section */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 20,
              marginBottom: 32
            }}>
              {requiredFields.map(renderField)}
            </div>

            {/* Other Details Section */}
            <div style={{
              marginTop: 40,
              marginBottom: 32,
              paddingTop: 24,
              borderTop: '2px solid #e5e7eb'
            }}>
              <h2 style={{
                color: '#667eea',
                fontSize: 20,
                fontWeight: 700,
                marginBottom: 20,
                paddingBottom: 12,
                borderBottom: '2px solid #e5e7eb'
              }}>
                Other Details
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: 20
              }}>
                {otherDetailsFields.map(renderField)}
              </div>
            </div>

            {/* Bank Details Section */}
            <div style={{
              marginTop: 40,
              marginBottom: 32,
              paddingTop: 24,
              borderTop: '2px solid #e5e7eb'
            }}>
              <h2 style={{
                color: '#667eea',
                fontSize: isMobile ? 18 : 20,
                fontWeight: 700,
                marginBottom: 20,
                paddingBottom: 12,
                borderBottom: '2px solid #e5e7eb'
              }}>
                Bank Details
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: 20
              }}>
                {bankDetailsFields.map(renderField)}
              </div>
            </div>

            {/* Documents Section */}
            <div style={{
              marginTop: 40,
              marginBottom: 32,
              paddingTop: 24,
              borderTop: '2px solid #e5e7eb',
              position: 'relative'
            }}>
              <h2 style={{
                color: '#667eea',
                fontSize: isMobile ? 18 : 20,
                fontWeight: 700,
                marginBottom: 20,
                paddingBottom: 12,
                borderBottom: '2px solid #e5e7eb'
              }}>
                Documents <span style={{ fontSize: 14, fontWeight: 400, color: '#9ca3af' }}>(Optional)</span>
              </h2>
              
              {/* Sticky Aadhar Note - This will stick to top when scrolling */}
              <div 
                data-sticky-note
                style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 100,
                  backgroundColor: '#fffbeb',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '2px solid #fef3c7',
                  marginBottom: 20,
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  width: '100%',
                  boxSizing: 'border-box'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12
                }}>
                  <div style={{
                    flexShrink: 0,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    background: '#fbbf24',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 600
                  }}>
                    !
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{
                      color: '#92400e',
                      fontSize: isMobile ? 13 : 14,
                      fontWeight: 600,
                      margin: '0 0 6px 0',
                      lineHeight: 1.5
                    }}>
                      Aadhar Verification Notice
                    </p>
                    <p style={{
                      color: '#78350f',
                      fontSize: isMobile ? 12 : 13,
                      margin: 0,
                      lineHeight: 1.6
                    }}>
                      The Aadhar document you upload is <strong>only used for verification purposes</strong>. Once uploaded and verified, the Aadhar document will <strong>not be saved permanently</strong> in our database. Your privacy and data security are our priority.
                    </p>
                  </div>
                </div>
              </div>
              
              <p style={{ color: '#6b7280', fontSize: isMobile ? 12 : 14, marginBottom: 20 }}>
                Upload documents in image (JPG, PNG, WEBP), PDF, or DOCX format. Maximum file size: 10MB.
              </p>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 16
              }}>
                {/* Aadhar */}
                <div style={{
                  padding: 16,
                  border: '2px solid #e5e7eb',
                  borderRadius: 12,
                  background: '#fff'
                }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: isMobile ? 13 : 14 }}>
                    Aadhar Card <span style={{ color: '#9ca3af', fontWeight: 400 }}>(Optional)</span>
                  </label>
                  {documents.aadhar ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      <FaFileAlt style={{ color: '#667eea', fontSize: 20 }} />
                      <span style={{ flex: 1, fontSize: isMobile ? 11 : 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {documents.aadhar.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveDocument('aadhar')}
                        style={{
                          background: '#fee2e2',
                          border: 'none',
                          borderRadius: 6,
                          padding: '4px 8px',
                          cursor: 'pointer',
                          color: '#dc2626'
                        }}
                      >
                        <FaTrash style={{ fontSize: 12 }} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, marginTop: 8 }}>
                      <input
                        type="file"
                        id="aadhar-upload"
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={(e) => handleDocumentChange('aadhar', e.target.files[0])}
                        style={{ display: 'none' }}
                      />
                      <label
                        htmlFor="aadhar-upload"
                        style={{
                          flex: 1,
                          padding: '12px',
                          border: '2px dashed #cbd5e1',
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f8fafc',
                          fontSize: isMobile ? 12 : 14,
                          color: '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8
                        }}
                      >
                        <FaUpload /> Upload from Device
                      </label>
                      <button
                        type="button"
                        onClick={() => startCamera('aadhar')}
                        style={{
                          padding: '12px',
                          border: '2px solid #667eea',
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f0f4ff',
                          fontSize: isMobile ? 12 : 14,
                          color: '#667eea',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          fontWeight: 600
                        }}
                      >
                        <FaCamera /> Capture
                      </button>
                    </div>
                  )}
                </div>

                {/* School Certificate (Optional) */}
                <div style={{
                  padding: 16,
                  border: '2px solid #e5e7eb',
                  borderRadius: 12,
                  background: '#fff'
                }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: isMobile ? 13 : 14 }}>
                    School Certificate <span style={{ color: '#9ca3af', fontWeight: 400 }}>(Optional)</span>
                  </label>
                  {documents.school_certificate ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      <FaFileAlt style={{ color: '#667eea', fontSize: 20 }} />
                      <span style={{ flex: 1, fontSize: isMobile ? 11 : 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {documents.school_certificate.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveDocument('school_certificate')}
                        style={{
                          background: '#fee2e2',
                          border: 'none',
                          borderRadius: 6,
                          padding: '4px 8px',
                          cursor: 'pointer',
                          color: '#dc2626'
                        }}
                      >
                        <FaTrash style={{ fontSize: 12 }} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, marginTop: 8 }}>
                      <input
                        type="file"
                        id="school-certificate-upload"
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={(e) => handleDocumentChange('school_certificate', e.target.files[0])}
                        style={{ display: 'none' }}
                      />
                      <label
                        htmlFor="school-certificate-upload"
                        style={{
                          flex: 1,
                          padding: '12px',
                          border: '2px dashed #cbd5e1',
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f8fafc',
                          fontSize: isMobile ? 12 : 14,
                          color: '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8
                        }}
                      >
                        <FaUpload /> Upload from Device
                      </label>
                      <button
                        type="button"
                        onClick={() => startCamera('school_certificate')}
                        style={{
                          padding: '12px',
                          border: '2px solid #667eea',
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f0f4ff',
                          fontSize: isMobile ? 12 : 14,
                          color: '#667eea',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          fontWeight: 600
                        }}
                      >
                        <FaCamera /> Capture
                      </button>
                    </div>
                  )}
                </div>

                {/* Educational Certificate (Graduate/ITI/Diploma) */}
                <div style={{
                  padding: 16,
                  border: '2px solid #e5e7eb',
                  borderRadius: 12,
                  background: '#fff'
                }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: isMobile ? 13 : 14 }}>
                    Educational Certificate (Graduate/ITI/Diploma) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(Optional)</span>
                  </label>
                  {documents.educational_certificate ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      <FaFileAlt style={{ color: '#667eea', fontSize: 20 }} />
                      <span style={{ flex: 1, fontSize: isMobile ? 11 : 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {documents.educational_certificate.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveDocument('educational_certificate')}
                        style={{
                          background: '#fee2e2',
                          border: 'none',
                          borderRadius: 6,
                          padding: '4px 8px',
                          cursor: 'pointer',
                          color: '#dc2626'
                        }}
                      >
                        <FaTrash style={{ fontSize: 12 }} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, marginTop: 8 }}>
                      <input
                        type="file"
                        id="educational-certificate-upload"
                        accept="image/*,.pdf,.doc,.docx"
                        onChange={(e) => handleDocumentChange('educational_certificate', e.target.files[0])}
                        style={{ display: 'none' }}
                      />
                      <label
                        htmlFor="educational-certificate-upload"
                        style={{
                          flex: 1,
                          padding: '12px',
                          border: '2px dashed #cbd5e1',
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f8fafc',
                          fontSize: isMobile ? 12 : 14,
                          color: '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8
                        }}
                      >
                        <FaUpload /> Upload from Device
                      </label>
                      <button
                        type="button"
                        onClick={() => startCamera('educational_certificate')}
                        style={{
                          padding: '12px',
                          border: '2px solid #667eea',
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f0f4ff',
                          fontSize: isMobile ? 12 : 14,
                          color: '#667eea',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          fontWeight: 600
                        }}
                      >
                        <FaCamera /> Capture
                      </button>
                    </div>
                  )}
                </div>

                {/* Bank Passbook */}
                <div style={{
                  padding: 16,
                  border: '2px solid #e5e7eb',
                  borderRadius: 12,
                  background: '#fff'
                }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: isMobile ? 13 : 14 }}>
                    Bank Passbook (Front Page) <span style={{ color: '#9ca3af', fontWeight: 400 }}>(Optional)</span>
                  </label>
                  {documents.bank_passbook ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                      <FaFileAlt style={{ color: '#667eea', fontSize: 20 }} />
                      <span style={{ flex: 1, fontSize: isMobile ? 11 : 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {documents.bank_passbook.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveDocument('bank_passbook')}
                        style={{
                          background: '#fee2e2',
                          border: 'none',
                          borderRadius: 6,
                          padding: '4px 8px',
                          cursor: 'pointer',
                          color: '#dc2626'
                        }}
                      >
                        <FaTrash style={{ fontSize: 12 }} />
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, marginTop: 8 }}>
                      <input
                        type="file"
                        id="bank-passbook-upload"
                        accept="image/*,.pdf"
                        onChange={(e) => handleDocumentChange('bank_passbook', e.target.files[0])}
                        style={{ display: 'none' }}
                      />
                      <label
                        htmlFor="bank-passbook-upload"
                        style={{
                          flex: 1,
                          padding: '12px',
                          border: '2px dashed #cbd5e1',
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f8fafc',
                          fontSize: isMobile ? 12 : 14,
                          color: '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8
                        }}
                      >
                        <FaUpload /> Upload from Device
                      </label>
                      <button
                        type="button"
                        onClick={() => startCamera('bank_passbook')}
                        style={{
                          padding: '12px',
                          border: '2px solid #667eea',
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f0f4ff',
                          fontSize: isMobile ? 12 : 14,
                          color: '#667eea',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          fontWeight: 600
                        }}
                      >
                        <FaCamera /> Capture
                      </button>
                    </div>
                  )}
                </div>

                {/* Candidate Image */}
                <div style={{
                  padding: 16,
                  border: '2px solid #e5e7eb',
                  borderRadius: 12,
                  background: '#fff'
                }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: isMobile ? 13 : 14 }}>
                    Candidate Photo <span style={{ color: '#9ca3af', fontWeight: 400 }}>(Optional)</span>
                  </label>
                  {documents.candidate_image ? (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        {documents.candidate_image.type.startsWith('image/') && (
                          <img
                            src={URL.createObjectURL(documents.candidate_image)}
                            alt="Candidate"
                            style={{
                              width: 60,
                              height: 60,
                              borderRadius: 8,
                              objectFit: 'cover',
                              border: '2px solid #e5e7eb'
                            }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: isMobile ? 11 : 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {documents.candidate_image.name}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveDocument('candidate_image')}
                          style={{
                            background: '#fee2e2',
                            border: 'none',
                            borderRadius: 6,
                            padding: '4px 8px',
                            cursor: 'pointer',
                            color: '#dc2626'
                          }}
                        >
                          <FaTrash style={{ fontSize: 12 }} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, marginTop: 8 }}>
                      <input
                        type="file"
                        id="candidate-image-upload"
                        accept="image/*"
                        onChange={(e) => handleDocumentChange('candidate_image', e.target.files[0])}
                        style={{ display: 'none' }}
                      />
                      <label
                        htmlFor="candidate-image-upload"
                        style={{
                          flex: 1,
                          padding: '12px',
                          border: '2px dashed #cbd5e1',
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f8fafc',
                          fontSize: isMobile ? 12 : 14,
                          color: '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8
                        }}
                      >
                        <FaUpload /> Upload from Device
                      </label>
                      <button
                        type="button"
                        onClick={() => startCamera('candidate_image')}
                        style={{
                          padding: '12px',
                          border: '2px solid #667eea',
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f0f4ff',
                          fontSize: isMobile ? 12 : 14,
                          color: '#667eea',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          fontWeight: 600
                        }}
                      >
                        <FaCamera /> Capture
                      </button>
                    </div>
                  )}
                </div>

                {/* PAN Card (Optional) */}
                <div style={{
                  padding: 16,
                  border: '2px solid #e5e7eb',
                  borderRadius: 12,
                  background: '#fff'
                }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: isMobile ? 13 : 14 }}>
                    PAN Card <span style={{ color: '#9ca3af', fontWeight: 400 }}>(Optional)</span>
                  </label>
                  {documents.pancard ? (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: isMobile ? 11 : 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {documents.pancard.name}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveDocument('pancard')}
                          style={{
                            background: '#fee2e2',
                            border: 'none',
                            borderRadius: 6,
                            padding: '4px 8px',
                            cursor: 'pointer',
                            color: '#dc2626'
                          }}
                        >
                          <FaTrash style={{ fontSize: 12 }} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, marginTop: 8 }}>
                      <input
                        type="file"
                        id="pancard-upload"
                        accept="image/*,.pdf"
                        onChange={(e) => handleDocumentChange('pancard', e.target.files[0])}
                        style={{ display: 'none' }}
                      />
                      <label
                        htmlFor="pancard-upload"
                        style={{
                          flex: 1,
                          padding: '12px',
                          border: '2px dashed #cbd5e1',
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f8fafc',
                          fontSize: isMobile ? 12 : 14,
                          color: '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8
                        }}
                      >
                        <FaUpload /> Upload from Device
                      </label>
                      <button
                        type="button"
                        onClick={() => startCamera('pancard')}
                        style={{
                          padding: '12px',
                          border: '2px solid #667eea',
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f0f4ff',
                          fontSize: isMobile ? 12 : 14,
                          color: '#667eea',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          fontWeight: 600
                        }}
                      >
                        <FaCamera /> Capture
                      </button>
                    </div>
                  )}
                </div>

                {/* Signature (Optional) */}
                <div style={{
                  padding: 16,
                  border: '2px solid #e5e7eb',
                  borderRadius: 12,
                  background: '#fff'
                }}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 600, color: '#374151', fontSize: isMobile ? 13 : 14 }}>
                    Signature <span style={{ color: '#9ca3af', fontWeight: 400 }}>(Optional)</span>
                  </label>
                  {documents.signature ? (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        {documents.signature.type?.startsWith('image/') && (
                          <img
                            src={URL.createObjectURL(documents.signature)}
                            alt="Signature"
                            style={{
                              width: 60,
                              height: 60,
                              borderRadius: 8,
                              objectFit: 'cover',
                              border: '2px solid #e5e7eb'
                            }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: isMobile ? 11 : 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {documents.signature.name}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveDocument('signature')}
                          style={{
                            background: '#fee2e2',
                            border: 'none',
                            borderRadius: 6,
                            padding: '4px 8px',
                            cursor: 'pointer',
                            color: '#dc2626'
                          }}
                        >
                          <FaTrash style={{ fontSize: 12 }} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 8, marginTop: 8 }}>
                      <input
                        type="file"
                        id="signature-upload"
                        accept="image/*,.pdf"
                        onChange={(e) => handleDocumentChange('signature', e.target.files[0])}
                        style={{ display: 'none' }}
                      />
                      <label
                        htmlFor="signature-upload"
                        style={{
                          flex: 1,
                          padding: '12px',
                          border: '2px dashed #cbd5e1',
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f8fafc',
                          fontSize: isMobile ? 12 : 14,
                          color: '#64748b',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8
                        }}
                      >
                        <FaUpload /> Upload from Device
                      </label>
                      <button
                        type="button"
                        onClick={() => startCamera('signature')}
                        style={{
                          padding: '12px',
                          border: '2px solid #667eea',
                          borderRadius: 8,
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: '#f0f4ff',
                          fontSize: isMobile ? 12 : 14,
                          color: '#667eea',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 8,
                          fontWeight: 600
                        }}
                      >
                        <FaCamera /> Capture
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Camera Modal */}
      {showCameraModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20
        }}>
          <div style={{
            width: '100%',
            maxWidth: isMobile ? '100%' : 640,
            background: '#000',
            borderRadius: 12,
            overflow: 'hidden',
            position: 'relative'
          }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
            <div style={{
              position: 'absolute',
              bottom: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: 12
            }}>
              <button
                type="button"
                onClick={capturePhoto}
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  border: '4px solid #fff',
                  background: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }}
              >
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: '#fff',
                  border: '2px solid #667eea'
                }} />
              </button>
            </div>
            <button
              type="button"
              onClick={stopCamera}
              style={{
                position: 'absolute',
                top: 20,
                right: 20,
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '50%',
                width: 40,
                height: 40,
                color: '#fff',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20
              }}
            >
              <FaTimes />
            </button>
          </div>
          <p style={{ color: '#fff', marginTop: 16, textAlign: 'center', fontSize: 14 }}>
            Position yourself in the frame and tap the button to capture
          </p>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        /* Ensure sticky positioning works */
        [data-sticky-note] {
          position: -webkit-sticky !important;
          position: sticky !important;
        }
      `}</style>
    </div>
  );
}

export default CandidateRegisterForm;

